import {
	type AiAssistPlanOperation,
	type AiAssistPlanOutcome,
	type AiAssistPlanPrimitives,
	executeAiAssistPlan,
} from "@southfields-digital/mxmz-react-ai-assist/graphics";

import api from "src/services/graphicsApi";

type GraphicApiResponse = void | { data: PlaylistGraphic };

type PlanSnapshot = {
	playlistId: string;
	playlistGraphics: PlaylistGraphic[];
	templates: Graphic[];
};

/**
 * Routes the package's plan runner at the same endpoints a click uses. Reads are taken from
 * one snapshot: nothing here writes to the store, and only one plan runs at a time, so
 * re-reading between operations could not see anything new.
 */
const primitivesFor = ({
	playlistId,
	playlistGraphics,
	templates,
}: PlanSnapshot): AiAssistPlanPrimitives<PlaylistGraphic> => ({
	getOrder: () =>
		playlistGraphics
			.filter((graphic) => graphic.playlist?.id === playlistId)
			.map((graphic) => graphic.id),

	getGraphic: (id) => playlistGraphics.find((graphic) => graphic.id === id),

	getTemplate: (id) => templates.find((template) => template.id === id),

	setFields: async (id, propertiesValues) => {
		const response = (await api.patch(`/playlists/${playlistId}/graphics/${id}`, {
			propertiesValues,
		})) as GraphicApiResponse;

		return response?.data;
	},

	createGraphic: async ({ templateId, name, fields }) => {
		const response = (await api.post(`/playlists/${playlistId}/graphics`, {
			graphicId: templateId,
			...(name ? { name } : {}),
			propertiesValues: fields,
		})) as GraphicApiResponse;

		return response?.data;
	},

	reorder: async (graphics) => {
		await api.patch(`/playlists/${playlistId}/graphics/reorder`, { graphics });
	},
});

export const applyPlanWith = (
	operations: AiAssistPlanOperation[],
	snapshot: PlanSnapshot,
): Promise<AiAssistPlanOutcome<PlaylistGraphic>> =>
	executeAiAssistPlan(operations, primitivesFor(snapshot));
